// ============================================================================
// DANCE STUDIO DATA
// ============================================================================
// Consolidated data file containing all static data and in-memory database

import { DanceClass } from '../core/types'

// ============================================================================
// DANCE CLASSES DATA
// ============================================================================

// Static dance classes data
export const danceClasses: DanceClass[] = [
  {
    id: 1,
    name: 'Special Gems: Dance and Movement Therapy',
    description: 'A Dance program designed for individuals with special needs, focusing on movement, coordination, and self-expression.',
    longDescription: 'Our Special Gems program (Dance and Movement Therapy) is a specialized therapeutic approach that uses movement and dance to support emotional, cognitive, physical, and social integration. This evidence-based practice is particularly beneficial for individuals with special needs, including those with autism spectrum disorders, ADHD, developmental disabilities, and neurological conditions.\n\nSessions focus on non-verbal communication, sensory integration, motor skill development, and emotional expression through structured movement activities. Trained therapists create safe, supportive environments where participants can explore movement without judgment, building confidence and communication skills.\n\nThe therapy incorporates elements from various dance traditions, adapted to meet individual needs and abilities. Benefits include improved coordination, enhanced social interaction, better emotional regulation, and increased self-awareness. Family involvement is often encouraged to extend therapeutic benefits beyond sessions.',
    duration: 60,
    level: 'All Levels',
    price: 1000,
    image: '/images/classes/special-dance-full.png',
    category: 'special',
    isActive: true,
    maxCapacity: 10,
    // Extended details
    features: [
      'Individualized movement plans',
      'Sensory integration techniques',
      'Non-verbal communication methods',
      'Emotional regulation strategies',
      'Progress tracking'
    ],
    benefits: [
      'Improved motor coordination',
      'Enhanced social interaction',
      'Increased self-confidence',
      'Better emotional regulation',
      'Physical fitness development'
    ],
    deliveryMode: 'Online',
    instructor: 'Dr. Sophia Mohan',
    requirements: 'Comfortable clothing, water bottle, any mobility aids if needed',
    ageGroup: 'All ages (specialized for special needs)',
    equipment: 'All equipment provided, adaptive tools available'
  },
  {
    id: 2,
    name: 'Bharatanatyam & Semi-Classical',
    description: 'Learn the graceful and expressive classical Indian dance form of Bharatanatyam along with semi-classical fusion styles.',
    longDescription: 'Immerse yourself in the rich cultural heritage of Bharatanatyam, it\'s one of India\'s most ancient classical dance forms, originating in Tamil Nadu temples over 2000 years ago. This sophisticated art form combines intricate footwork, expressive hand gestures (mudras), facial expressions (abhinaya), and storytelling through movement.\n\nTraining encompasses technical precision, spiritual understanding, and cultural appreciation. Students learn traditional compositions, mythological stories, and the philosophical foundations underlying this sacred dance. Semi-classical dance bridges traditional forms with contemporary expression, maintaining classical techniques while allowing creative interpretation.\n\nBoth styles develop discipline, grace, cultural awareness, and artistic expression. Training typically follows a structured curriculum progressing from basic positions to complex choreographic pieces. Regular practice builds strength, flexibility, coordination, and stage presence. Performance opportunities in recitals and cultural events provide platforms for artistic growth and community engagement.',
    duration: 60,
    level: 'Beginner+',
    price: 1500,
    image: '/images/classes/classical-dance-full.png',
    category: 'classical',
    isActive: true,
    maxCapacity: 5,
    // Extended details
    features: [
      'Traditional Bharatanatyam techniques',
      'Semi-classical fusion styles',
      'Mudras and expressions',
      'Rhythm and footwork patterns',
      'Cultural context learning'
    ],
    benefits: [
      'Improved posture and physical grace',
      'Enhanced rhythm sense',
      'Cultural knowledge deepening',
      'Physical fitness and Confidence building',
      'Improved body discipline'
    ],
    deliveryMode: 'Online',
    instructor: 'Dr. Sophia Mohan',
    requirements: 'comfortable practice clothes, Water Bottle',
    ageGroup: '8+ years',
    equipment: 'NONE'
  },
  {
    id: 3,
    name: 'Lazy Group Dance for Senior Citizens',
    description: 'Gentle dance sessions designed specifically for senior citizens, focusing on mobility, balance, and social connection.',
    longDescription: 'Our Lazy Group Dance program is specially crafted for senior citizens who want to stay active, social, and healthy in a comfortable, supportive environment. These sessions prioritize fun and social connection over technical precision or physical intensity. Movements are gentle, adaptable, and can be performed seated or standing with support, accommodating various mobility levels and health conditions.\n\nThe social aspect is equally important, combating isolation and creating opportunities for friendship and community building. Sessions typically include familiar music from participants\' younger years, simple choreographed sequences, and plenty of social interaction time.\n\nHealth benefits include improved circulation, enhanced balance and coordination, cognitive stimulation through learning new patterns, and mood enhancement. The low-pressure environment removes performance anxiety, allowing participants to focus on enjoyment and personal expression while maintaining physical activity appropriate for their age and abilities.',
    duration: 60,
    level: 'All Levels',
    price: 1200,
    image: '/images/classes/seniors-dance-full.png',
    category: 'seniors',
    isActive: true,
    maxCapacity: 20,
    // Extended details
    features: [
      'Gentle movements',
      'Social interaction',
      'Mobility and Balance improvement',
      'Memory-stimulating choreography',
      'Group activities'
    ],
    benefits: [
      'Reduced social isolation',
      'Improved physical mobility',
      'Enhanced cognitive function',
      'Stress relief',
      'Fun and enjoyment'
    ],
    deliveryMode: 'Online',
    instructor: 'Dr. Sophia Mohan',
    requirements: 'Comfortable clothes, water bottle, any mobility aids',
    ageGroup: '50+ years',
    equipment: 'Comfortable chairs, gentle music, safety equipment (if needed)'
  },
  {
    id: 4,
    name: 'Zumba and Yoga Fitness',
    description: 'High-energy Zumba fitness classes combined with yoga for a complete mind-body workout, perfect for all fitness levels.',
    longDescription: 'Experience the perfect fusion of high-energy Zumba and calming yoga in this dynamic fitness program designed to give you the best of both worlds. Our Zumba and Yoga Fitness class combines the exhilarating cardio workout of Zumba with the mindful, restorative benefits of yoga, creating a comprehensive mind-body experience.\n\nThe first half of each session features energetic Zumba routines set to infectious Latin and international music, designed to get your heart pumping and calories burning while having an absolute blast. You\'ll learn easy-to-follow dance moves that work your entire body, improve coordination, and boost your mood naturally.\n\nThe second half transitions into gentle yoga flows and poses that help you cool down, stretch your muscles, and find inner peace. This unique combination provides cardiovascular benefits, strength training, flexibility improvement, and stress relief all in one session. Whether you\'re a fitness beginner or an experienced athlete, our instructors provide modifications for every fitness level, ensuring everyone can participate safely and effectively while having fun.',
    duration: 60,
    level: 'All Levels',
    price: 1500,
    image: '/images/classes/fitness-dance-full.jpeg',
    category: 'fitness',
    isActive: true,
    maxCapacity: 25,
    // Extended details
    features: [
      'Zumba dance moves',
      'Yoga stretches and breathing techniques',
      'Mind-body connection practices',
      'Cardio workout',
      'Group fitness'
    ],
    benefits: [
      'Cardiovascular fitness',
      'Better body coordination',
      'Improved flexibility',
      'Stress relief and Mental wellness',
      'Social interaction'
    ],
    deliveryMode: 'Online',
    instructor: 'Dr. Sophia Mohan',
    requirements: 'Comfortable workout clothes, Water Bottle, Yoga mat',
    ageGroup: '16+ years',
    equipment: 'Yoga mats, fitness equipment (if needed)'
  },
  {
    id: 5,
    name: 'Events and Choreography',
    description: 'Professional choreography and event performance classes, perfect for weddings, parties, and special occasions.',
    longDescription: 'Transform your special moments into unforgettable experiences with our comprehensive Events and Choreography program. This specialized program is designed for those who want to create stunning dance performances for weddings, parties, corporate events, and other special occasions.\n\nOur expert choreographers work closely with you to understand your vision, event theme, and performance goals, creating custom choreography that perfectly matches your celebration. Whether you\'re planning a wedding dance, a surprise performance, or a group routine for a special event, we provide complete choreography services from concept to execution.\n\nThe program covers everything from music selection and timing to costume coordination and stage management. We specialize in various dance styles including Bollywood, contemporary, classical, and fusion, ensuring your performance reflects your personal style and cultural preferences. Our experienced team handles all aspects of event choreography, including group coordination, rehearsal scheduling, and performance day management. We work with participants of all skill levels, from complete beginners to experienced dancers, ensuring everyone looks confident and polished on the big day.',
    duration: 240,
    level: 'All Levels',
    price: 8000,
    image: '/images/classes/choreography-dance-full.png',
    category: 'choreography',
    isActive: true,
    maxCapacity: 5,
    // Extended details
    features: [
      'Custom Choreography',
      'Event production',
      'Group coordination',
      'Performance preparation',
      'Wedding Choreography',
    ],
    benefits: [
      'Professional performance',
      'Event coordination',
      'Team building',
      'Creative expression for events',
      'Confidence building'
    ],
    deliveryMode: 'Online',
    instructor: 'Dr. Sophia Mohan (Event Choreographer)',
    requirements: 'Event-appropriate attire, prior knowledge of the event, any special requirements',
    ageGroup: 'All ages',
    equipment: 'NONE'
  },
  {
    id: 6,
    name: 'Art and Craft Workshops',
    description: 'Creative art and craft workshops combined with dance, exploring the connection between visual arts and movement.',
    longDescription: 'Discover the beautiful intersection of visual arts and movement in our innovative Art and Craft Workshops program. This unique program combines creative art-making with dance, allowing participants to explore their artistic expression through both visual and kinesthetic mediums.\n\nEach session begins with gentle movement exercises that inspire creativity and help participants connect with their inner artist. You\'ll learn various art techniques including mandala creation, sketching, painting, and art using Canvas, all while incorporating movement and dance elements that enhance your creative process.\n\nThe program emphasizes the therapeutic benefits of art-making, helping participants reduce stress, improve focus, and develop mindfulness through creative expression. Our experienced instructors guide you through different artistic styles and techniques, encouraging experimentation and personal growth. The workshops include both individual projects and collaborative group exercises, fostering a supportive community of artists and dancers. Whether you\'re a complete beginner or an experienced artist, this program provides a safe, encouraging space to explore your creativity while staying physically active and mentally engaged.',
    duration: 60,
    level: 'All Levels',
    price: 500,
    image: '/images/classes/art-craft-full.png',
    category: 'art',
    isActive: true,
    maxCapacity: 10,
    // Extended details
    features: [
      'Basic Artistic development',
      'Mandala Artwork and coloring',
      'Sketching and painting basics',
      'Canvas Artwork and coloring',
      'Creative expression Art'
    ],
    benefits: [
      'Creative expression',
      'Improved Artistic skills',
      'Motor development',
      'Personal growth',
      'Imagination'
    ],
    deliveryMode: 'Online',
    instructor: 'Dr. Sophia Mohan',
    requirements: 'Comfortable clothes, Art and craft materials',
    ageGroup: '5+ years',
    equipment: 'Art and craft materials'
  }
]

// ============================================================================
// CORE DATA FUNCTIONS
// ============================================================================

/**
 * Get all active dance classes
 */
export const getClasses = (): DanceClass[] => {
  return danceClasses.filter(c => c.isActive)
}

/**
 * Get dance class by ID
 */
export const getClassById = (id: number): DanceClass | undefined => {
  return danceClasses.find(c => c.id === id)
}


